import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import EventCard from '../discover/EventCard';
import { toast } from 'react-toastify';
import SingleEvent from '../../classes/events/SingleEvent';
import EventEmptyStateIcon from './EventEmptyStateIcon';
import './InvitationsView.css';

function InvitationsView({ refreshKey = 0, onSwitchToUpcoming, isActive = true }) {
  const { t } = useTranslation(['events', 'shared']);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingIds, setProcessingIds] = useState(new Set());
  const channelRef = useRef(null);
  const refreshKeyRef = useRef(refreshKey);
  refreshKeyRef.current = refreshKey;
  const loadedRefreshKeyRef = useRef(null);

  const loadInvitations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const today = new Date().toISOString().split('T')[0];

      // Load pending invitations for current user
      const { data: invitationsData, error: invitationsError } = await supabase
        .from('event_invitations')
        .select(`
          id,
          event_id,
          inviter_id,
          status,
          created_at,
          events!inner (
            id,
            title,
            description,
            event_date,
            start_time,
            duration_minutes,
            location,
            max_participants,
            is_free,
            host_id,
            banner_url,
            is_cancelled,
            status,
            event_type
          ),
          inviter:users!event_invitations_inviter_id_fkey (
            id,
            nickname,
            name,
            first_name,
            last_name,
            avatar_url
          )
        `)
        .eq('invitee_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (invitationsError) throw invitationsError;

      // Filter out cancelled events and past events
      const validInvitations = (invitationsData || []).filter(invitation => {
        const event = invitation.events;
        return event && 
               event.is_cancelled !== true && 
               event.event_date >= today;
      });

      setInvitations(validInvitations);
      loadedRefreshKeyRef.current = refreshKeyRef.current;
    } catch (err) {
      console.error('Error loading invitations:', err);
      setError(err.message || t('events.invitations.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleAcceptInvitation = async (invitationId, eventId) => {
    if (processingIds.has(invitationId)) return;

    try {
      setProcessingIds(prev => new Set(prev).add(invitationId));

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error(t('shared.common.notLoggedIn'));
        return;
      }

      // Check if user is already a participant
      const isParticipant = await SingleEvent.isParticipantInDB(eventId, user.id);
      if (isParticipant) {
        // If already participant, just update invitation status
        await supabase
          .from('event_invitations')
          .update({ status: 'accepted' })
          .eq('id', invitationId);
        
        toast.success(t('events.invitations.alreadyParticipant'));
        await loadInvitations();
        return;
      }

      // Check if event is full
      const eventResult = await SingleEvent.loadEvent(eventId);
      if (eventResult.data) {
        const { data: participants } = await supabase
          .from('event_participants')
          .select('id', { count: 'exact', head: true })
          .eq('event_id', eventId)
          .eq('status', 'active');

        const participantsCount = participants?.length || 0;
        if (eventResult.data.max_participants && participantsCount >= eventResult.data.max_participants) {
          toast.error(t('events.invitations.eventFull'));
          await loadInvitations();
          return;
        }
      }

      // Update invitation status to accepted
      const { error: updateError } = await supabase
        .from('event_invitations')
        .update({ status: 'accepted' })
        .eq('id', invitationId);

      if (updateError) throw updateError;

      // Create application (which will be auto-approved since user was invited)
      const { error: applicationError } = await supabase
        .from('event_applications')
        .insert({
          event_id: eventId,
          user_id: user.id,
          status: 'approved',
          message: t('events.invitations.applicationMessage', 'Zaproszenie zaakceptowane')
        });

      if (applicationError) {
        console.error('Error creating application:', applicationError);
        // If application already exists, that's okay
        if (applicationError.code !== '23505') {
          throw applicationError;
        }
      }

      // Create participant record
      const { error: participantError } = await supabase
        .from('event_participants')
        .insert({
          event_id: eventId,
          user_id: user.id,
          status: 'active',
          joined_at: new Date().toISOString()
        });

      if (participantError) {
        console.error('Error creating participant:', participantError);
        // If participant already exists, that's okay
        if (participantError.code !== '23505') {
          throw participantError;
        }
      }

      toast.success(t('events.invitations.accepted'));
      
      // Reload invitations
      await loadInvitations();
      
      // Switch to upcoming tab after a short delay
      if (onSwitchToUpcoming) {
        setTimeout(() => {
          onSwitchToUpcoming();
        }, 500);
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      toast.error(t('events.invitations.errorAccepting'));
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(invitationId);
        return newSet;
      });
    }
  };

  const handleRejectInvitation = async (invitationId) => {
    if (processingIds.has(invitationId)) return;

    try {
      setProcessingIds(prev => new Set(prev).add(invitationId));

      const { error } = await supabase
        .from('event_invitations')
        .update({ status: 'rejected' })
        .eq('id', invitationId);

      if (error) throw error;

      toast.success(t('events.invitations.rejected'));
      await loadInvitations();
    } catch (err) {
      console.error('Error rejecting invitation:', err);
      toast.error(t('events.invitations.errorRejecting'));
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(invitationId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    if (!isActive) {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      return undefined;
    }

    if (loadedRefreshKeyRef.current !== refreshKey) {
      loadInvitations();
    }

    let cancelled = false;

    const setupRealtimeSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;

      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }

      const channel = supabase
        .channel(`event-invitations-${user.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'event_invitations',
            filter: `invitee_id=eq.${user.id}`
          },
          async (payload) => {
            console.log('Invitation change detected:', payload);
            setTimeout(() => {
              loadInvitations();
            }, 300);
          }
        )
        .subscribe();

      if (!cancelled) {
        channelRef.current = channel;
      } else {
        supabase.removeChannel(channel);
      }
    };

    setupRealtimeSubscription();

    return () => {
      cancelled = true;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [isActive, refreshKey, loadInvitations]);

  if (loading) {
    return (
      <div className="invitations-view">
        <div className="invitations-view-loading">
          <p>{t('events.invitations.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invitations-view">
        <div className="invitations-view-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (invitations.length === 0) {
    return (
      <div className="invitations-view">
        <div className="invitations-view-empty invitations-view-empty-card">
          <div className="invitations-view-empty-icon" aria-hidden="true">
            <EventEmptyStateIcon variant="invitations" />
          </div>
          <h2>{t('events.invitations.title')}</h2>
          <p>{t('events.invitations.empty')}</p>
          <span className="invitations-view-empty-tip">
            {t('events.invitations.emptyTip', 'Gdy ktoś Cię zaprosi, zobaczysz to właśnie tutaj.')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="invitations-view">
      <div className="invitations-view-header">
        <div className="invitations-view-header-left">
          <h2>{t('events.invitations.title')}</h2>
          <p className="invitations-view-count">
            {invitations.length} {t('events.invitations.count')}
          </p>
        </div>
      </div>
      <div className="invitations-view-list">
        {invitations.map((invitation) => {
          const event = invitation.events;
          const inviter = invitation.inviter;
          const inviterName = inviter?.nickname || 
                             inviter?.name || 
                             `${inviter?.first_name || ''} ${inviter?.last_name || ''}`.trim() ||
                             t('events.invitations.unknownUser');

          return (
            <div key={invitation.id} className="invitation-item">
              <div className="invitation-item-event">
                <EventCard event={event} />
              </div>
              <div className="invitation-item-info">
                <div className="invitation-item-message">
                  <span className="invitation-item-inviter">{inviterName}</span>
                  {' '}
                  {t('events.invitations.invitedYou')}
                </div>
                <div className="invitation-item-actions">
                  <button
                    className="invitation-action-button invitation-action-accept"
                    onClick={() => handleAcceptInvitation(invitation.id, event.id)}
                    disabled={processingIds.has(invitation.id)}
                  >
                    {processingIds.has(invitation.id) ? t('common.loading') : t('events.invitations.accept')}
                  </button>
                  <button
                    className="invitation-action-button invitation-action-reject"
                    onClick={() => handleRejectInvitation(invitation.id)}
                    disabled={processingIds.has(invitation.id)}
                  >
                    {t('events.invitations.reject')}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default InvitationsView;
